import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import moment from "moment";
import DataTable from "react-data-table-component";
import Input from "../../../components/input";

/* MATERIAL TAILWIND COMPONENTS */
import { Button, Dialog, DialogBody } from "@material-tailwind/react";

/* ICONS */
import { FiTrash } from "react-icons/fi";
import { IoClose } from "react-icons/io5";

/* TOAST NOTIFICATION */
import { toast } from "react-toast";

/* API IMPORT */
import {
  getNewsLetterList,
  deleteNewsLetterById,
} from "../../../service/api/newsLetter.api";
import { handleAddToken } from "../../../service/request";
import { useSelector } from "react-redux";

const Newsletter = () => {
  // GLOBAL VARIABLES
  const userDetails = useSelector((state) => state?.user);

  // STATE VARIABLES
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(10);
  const [newsLetterList, setNewsLetterList] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);

  /* FUNCTIONS */
  const handlePageChange = async (e) => {
    setPage(e);
  };

  const handlePerPageChange = async (e) => {
    setSizePerPage(e);
  };

  const getNewsLetterListFunction = async () => {
    await handleAddToken(userDetails?.token);
    setLoading(true);
    const params = {
      page: page,
      sizePerPage: sizePerPage,
      pagination: true,
    };
    if (search) {
      params.search = search;
    }

    getNewsLetterList(params)
      .then((res) => {
        setNewsLetterList(res?.data?.docs || []);
        setTotalDocs(res?.data?.totalDocs || 0);
      })
      .catch((e) => {
        console.error("Error fetching newsletter list:", e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* HANDLE DELETE */
  const handleDelete = async () => {
    try {
      await handleAddToken(userDetails?.token);
      const res = await deleteNewsLetterById(deleteId);
      toast.success(res?.message || "Newsletter subscription removed successfully");
      await getNewsLetterListFunction();
    } catch (e) {
      toast.error(e?.response?.data?.message || "An error occurred while deleting");
    } finally {
      setDeleteModal(false);
      setDeleteId(null);
    }
  };

  /* COLUMNS */
  const columns = [
    {
      name: "Sr. No",
      width: "12vh",
      selector: (row, index) => (
        <span className="text-sm font-proxima-semibold text-black text-opacity-60">
          {(page - 1) * sizePerPage + index + 1}
        </span>
      ),
    },
    {
      name: "Email Address",
      selector: (row) => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-proxima-semibold text-black">
            {row.email}
          </span>
        </div>
      ),
    },
    {
      name: "Subscribed On",
      width: "30vh",
      selector: (row) => (
        <span className="text-sm font-proxima-semibold text-black text-opacity-60">
          {moment(row.createdAt).format("MMM DD, YYYY hh:mm A")}
        </span>
      ),
    },
    {
      name: "Actions",
      width: "15vh",
      selector: (row) => (
        <div className="flex items-center">
          <Button
            onClick={() => {
              setDeleteId(row?._id);
              setDeleteModal(true);
            }}
            className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
          >
            <FiTrash className="w-4 h-4 text-greyText" />
          </Button>
        </div>
      ),
    },
  ];

  /* USE-EFFECTS */
  useEffect(() => {
    getNewsLetterListFunction();
  }, [page, sizePerPage, search]);

  /* LOADING SCREEN FOR DATATABLE */
  const LoadingScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-proxima-semibold text-black text-center">
          Please wait!
        </p>
        <p className="text-sm font-proxima-semibold text-black text-center">
          We are fetching newsletter subscribers...
        </p>
      </div>
    );
  };

  const NoDataScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-proxima-semibold text-black text-center">
          There are no newsletter subscribers to display.
        </p>
      </div>
    );
  };

  return (
    <section className="px-0 py-2 xl:p-10 lg:p-10 min-h-screen xl:min-h-full lg:min-h-full">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Newsletter Subscribers | CIDA Perfumes</title>
      </Helmet>

      <div className="container-full-width">
        {/* HEADING */}
        <div className="block xl:flex lg:flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-proxima-bold text-black tracking-tight">
              Newsletter
            </h1>
            <h2 className="text-lg font-proxima-regular text-greyText tracking-tight">
              View and manage customer newsletter subscriptions.
            </h2>
          </div>
          <div className="block xl:flex lg:flex items-center gap-2">
            {/* SEARCHBAR */}
            <div className="w-3/4 xl:w-full lg:w-full mt-2 xl:mt-0 lg:mt-0">
              <Input
                type="text"
                placeholder="Search by email..."
                name="search"
                id="search"
                value={search}
                onChange={(e) => setSearch(e?.target?.value)}
              />
            </div>
          </div>
        </div>

        <div className="mt-5">
          <DataTable
            className="data-table"
            columns={columns}
            data={totalDocs > 0 ? newsLetterList : []}
            progressPending={loading}
            progressComponent={<LoadingScreen />}
            pagination
            paginationServer
            paginationTotalRows={totalDocs}
            onChangeRowsPerPage={handlePerPageChange}
            onChangePage={handlePageChange}
            noDataComponent={<NoDataScreen />}
          />
        </div>

        <Dialog
          open={deleteModal}
          handler={() => setDeleteModal(!deleteModal)}
          size="xs"
          animate={{
            mount: { scale: 1, y: 0 },
            unmount: { scale: 0.9, y: -100 },
          }}
        >
          <DialogBody className="p-0">
            <div className="border-b border-authBorder px-5 py-3 flex items-center justify-between">
              <h5 className="text-lg font-proxima-bold text-black tracking-tight">
                Delete Newsletter Subscriber
              </h5>
              <Button
                className="p-2 rounded-full bg-white shadow-none hover:shadow-none hover:bg-black hover:bg-opacity-5"
                onClick={() => setDeleteModal(false)}
              >
                <IoClose className="w-4 h-4 text-greyText" />
              </Button>
            </div>
            <div className="p-5 border-b border-authBorder">
              <p className="text-base font-proxima-regular text-black">
                Are you sure you want to remove this subscriber from the newsletter list?
              </p>
            </div>
            <div className="flex items-center justify-end px-5 py-3">
              <Button
                onClick={handleDelete}
                className="text-sm shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-5 py-3 rounded-md font-proxima-semibold text-white transition-all duration-300 ease-in-out normal-case"
              >
                Delete subscriber
              </Button>
            </div>
          </DialogBody>
        </Dialog>
      </div>
    </section>
  );
};

export default Newsletter;
